import { useEffect, useRef, useState, useContext } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { auth, db, functions } from '../firebase';
import { maybePromptForPush } from '../services/softPushPrompt';
import { doc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { UserContext } from '../contexts/UserContext';
import { MembershipContext } from '../contexts/MembershipContext';
import { useContact } from '../contexts/ContactContext';
import { formatCurrencyInput } from '../utils/currencyUtils';
import { recordOfferIntentEvent, OFFER_INTENT_KINDS } from '../utils/offerIntentAnalytics';
import { createOfferSubmitTimer } from '../utils/offerSubmitTiming';
import AddressRequiredModal from '../components/AddressRequiredModal';
import { validateMessage } from '../utils/profanityFilter';
import { rememberLoginReturn } from '../utils/loginReturn';
import InactiveOwnerNotice from '../components/InactiveOwnerNotice';
import { placeholderFor, SKETCH_PLACEHOLDER_URL } from '../utils/listingPlaceholder';
import { allowsPurchase } from '../utils/offerMode';

const PurchaseOffer = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { openContact } = useContact();
  const targetPedalId = searchParams.get('targetPedalId');
  
  const [targetPedal, setTargetPedal] = useState(null);
  const [targetUser, setTargetUser] = useState(null);
  const [sellerInfo, setSellerInfo] = useState({
    username: '',
    badge: '',
    photoURL: '/images/default-avatar.png',
    membershipTier: 'free',
    averageRating: 0
  });
  const [offerAmount, setOfferAmount] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // The inactive-owner notice points its tail at this button.
  const submitButtonRef = useRef(null);
  const [error, setError] = useState('');
  const [offerSent, setOfferSent] = useState(false);
  const [showCostBreakdown, setShowCostBreakdown] = useState(false);
  const [costBreakdown, setCostBreakdown] = useState(null);
  const [calculatingCosts, setCalculatingCosts] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [calculatingSalesTax, setCalculatingSalesTax] = useState(false);
  
  const { firestoreUser, loading: userDataLoading } = useContext(UserContext);
  const { membershipTier, getCashFeePercentage: getMembershipCashFeePercentage } = useContext(MembershipContext);
  const currentUser = auth.currentUser;
  const [notLoggedIn, setNotLoggedIn] = useState(false);

  const [showAddressModal, setShowAddressModal] = useState(false);

  // Utility to check if address is complete (buyer needs full address so seller can ship to them)
  const isAddressComplete = (address) => {
    if (!address) return false;
    const required = ['name', 'street1', 'city', 'state', 'zip', 'country'];
    return required.every(field => address[field] && address[field].trim());
  };

  const addressComplete = !userDataLoading && isAddressComplete(firestoreUser?.shippingAddress);

  // Auto-open the inline address modal when the user lands here without a
  // complete address. Same trigger as the original "Shipping Address Required"
  // modal — the difference is that the modal now contains the address form
  // itself instead of redirecting to /profile.
  useEffect(() => {
    if (!userDataLoading && !addressComplete) {
      setShowAddressModal(true);
    } else {
      setShowAddressModal(false);
    }
  }, [userDataLoading, addressComplete]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) { setNotLoggedIn(true); setLoading(false); }
    });
    return () => unsubscribe();
  }, []);

  // Helper functions for badges (copied from PedalDetails)
  const getTradeBadge = (count) => {
    if (count === undefined || count === null || isNaN(count)) return '0';
    return count.toString();
  };

  const getMembershipBadge = (membershipTier) => {
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            <span className="bg-blue-600 text-white px-1.5 py-0.5 rounded font-bold mr-1" style={{ fontSize: '11px' }}>9V</span>
            Member
          </div>
        );
      case 'premium_plus':
        return (
          <div className="flex items-center bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            <span className="bg-purple-600 text-white px-1.5 py-0.5 rounded font-bold mr-1" style={{ fontSize: '11px' }}>18V</span>
            Member
          </div>
        );
      default:
        return (
          <div className="flex items-center bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            Free Member
          </div>
        );
    }
  };

  const getMembershipProfileBadge = (membershipTier) => {
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#2563eb',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)',
            }}
          >
            9V
          </div>
        );
      case 'premium_plus':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#7c3aed',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)',
            }}
          >
            18V
          </div>
        );
      default:
        return null;
    }
  };

  // Load target pedal and user data
  useEffect(() => {
    const loadData = async () => {
      if (!targetPedalId) {
        setLoading(false);
        return;
      }
      if (!currentUser) {
        // Auth session not restored yet (common on a fresh load, and especially
        // in the native WebView). Stay in the loading state instead of flashing
        // a premature empty/error screen. The onAuthStateChanged listener covers
        // the genuinely-logged-out case; once the session restores, currentUser
        // populates and this effect re-runs.
        return;
      }

      try {
        // Load target pedal
        const targetPedalDoc = await getDoc(doc(db, 'listings', targetPedalId));
        if (!targetPedalDoc.exists()) {
          if (!currentUser) {
            // Save current URL so user can return after login
            sessionStorage.setItem('returnAfterLogin', window.location.pathname);
            setError('Please log in to view this pedal. Click here to log in: /login');
          } else {
            setError('Target pedal not found');
          }
          setLoading(false);
          return;
        }
        const pedalData = { id: targetPedalDoc.id, ...targetPedalDoc.data() };
        if (!allowsPurchase(pedalData)) {
          // Before setTargetPedal: the error screen only renders while the
          // target is unset (review 2026-10-20).
          setError('This pedal is for trade only. The owner is not taking offers to buy it.');
          setLoading(false);
          return;
        }
        setTargetPedal(pedalData);

        // Load target user data
        // PII isolation: read sanitized public mirror.
        const targetUserDoc = await getDoc(doc(db, 'publicProfiles', pedalData.ownerId));
        if (targetUserDoc.exists()) {
          const userData = targetUserDoc.data();
          setTargetUser({ id: targetUserDoc.id, ...userData });
          
          // Set seller info for profile display
          setSellerInfo({
            username: userData.username || 'Unknown',
            badge: getTradeBadge(userData.completedTrades || 0),
            photoURL: userData.photoURL || '/images/default-avatar.png',
            membershipTier: userData.membershipTier || 'free',
            averageRating: userData.averageRating || 0
          });
        }

        // Pre-fill offer amount with pedal's asking price
        setOfferAmount(pedalData.price?.toString() || '');
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load pedal data');
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser, targetPedalId]);

  // Back to Top button scroll handler
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    const timer = createOfferSubmitTimer('purchase_offer', { listingId: targetPedalId });

    try {
      // Block if address is incomplete
      if (!isAddressComplete(firestoreUser?.shippingAddress)) {
        setShowAddressModal(true);
        setSubmitting(false);
        return;
      }

      if (!targetPedal) {
        throw new Error('Target pedal not found');
      }

      // 🔒 CRITICAL: Double-check that target pedal is not in trade before submitting
      const targetPedalDoc = await getDoc(doc(db, 'listings', targetPedalId));
      if (!targetPedalDoc.exists()) {
        throw new Error('Target pedal no longer exists');
      }
      
      const latestTargetPedalData = targetPedalDoc.data();
      if (latestTargetPedalData.inTrade) {
        throw new Error('This pedal is no longer available for purchase offers. It may have been accepted in another trade.');
      }
      if (!allowsPurchase(latestTargetPedalData)) {
        throw new Error('This pedal is for trade only. The owner is not taking offers to buy it.');
      }

      timer.mark('freshness_read');

      // Validate offer amount
      const rawAmount = Number(offerAmount);
      if (isNaN(rawAmount) || rawAmount <= 0) {
        throw new Error('Please enter a valid offer amount (must be positive)');
      }
      if (rawAmount > 2000) {
        throw new Error('Cash amount cannot exceed $2,000');
      }
      
      const validatedAmount = Math.round(rawAmount * 100) / 100;
      const safeNote = note.trim().slice(0, 500);

      if (safeNote) {
        const validation = validateMessage(safeNote);
        if (!validation.isValid) {
          throw new Error(validation.error);
        }
      }

      // Create the purchase offer
      const purchaseOfferData = {
        type: 'purchase',
        initiatorId: currentUser.uid,
        initiatorEmail: currentUser.email,
        responderId: targetPedal.ownerId,
        targetPedalId: targetPedalId,
        offerAmount: validatedAmount,
        note: safeNote,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        offerUpdatedAt: serverTimestamp(),
        history: [{
          type: 'offer',
          from: currentUser.uid,
          message: `Purchase offer of $${validatedAmount}${safeNote ? ` - ${safeNote}` : ''}`,
          timestamp: new Date()
        }]
      };

      const docRef = await addDoc(collection(db, 'purchaseOffers'), purchaseOfferData);

      timer.mark('write_ack', { purchaseOfferId: docRef.id });

      void recordOfferIntentEvent(OFFER_INTENT_KINDS.PURCHASE_OFFER_SUBMITTED, {
        listingId: targetPedalId,
        purchaseOfferId: docRef.id,
      });
      
      // Show success state
      setOfferSent(true);
      setSubmitting(false);

      // Contextual soft-ask for push permission (native-only, no-op on web).
      maybePromptForPush('make_offer');

      timer.mark('done');
      
      // Redirect to Trade Center after 2 seconds
      setTimeout(() => {
        navigate('/trade-center');
      }, 2000);
    } catch (err) {
      timer.fail(err);
      console.error('Error submitting purchase offer:', err);
      setError(err.message || 'Failed to submit purchase offer');
      setSubmitting(false);
    }
  };


  // Calculate cost breakdown
  const calculateCostBreakdown = async (amount) => {
    if (!amount || isNaN(amount) || amount <= 0) {
      setCostBreakdown(null);
      setShowCostBreakdown(false);
      return;
    }

    // Check if firestoreUser is loaded
    if (!firestoreUser) {
      console.log('⚠️ firestoreUser not loaded yet, skipping cost breakdown calculation');
      return;
    }

    setCalculatingCosts(true);
    
    try {
      const purchaseAmount = parseFloat(amount);
      const cashFeePercentage = getMembershipCashFeePercentage();
      const cashExchangeFee = Math.round(purchaseAmount * (cashFeePercentage / 100) * 100) / 100;
      
      const initialTotalAmount = purchaseAmount + cashExchangeFee;
      
      setCostBreakdown({
        purchaseAmount,
        cashExchangeFee,
        cashFeePercentage,
        salesTaxAmount: 0,
        salesTaxRate: 0,
        salesTaxState: null,
        totalAmount: initialTotalAmount
      });
      setShowCostBreakdown(true);
      setCalculatingCosts(false);
      
      if (firestoreUser?.shippingAddress) {
        setCalculatingSalesTax(true);
        calculateSalesTaxAsync(purchaseAmount, cashExchangeFee);
      }
    } catch (error) {
      console.error('Error calculating cost breakdown:', error);
      setCalculatingCosts(false);
    }
  };

  // Separate function for sales tax calculation
  const calculateSalesTaxAsync = async (purchaseAmount, cashExchangeFee) => {
    try {
      console.log('🔍 Sales tax calculation check:', {
        hasShippingAddress: !!firestoreUser?.shippingAddress,
        shippingAddress: firestoreUser?.shippingAddress,
        userState: firestoreUser?.shippingAddress?.state,
        isColorado: firestoreUser?.shippingAddress?.state === 'CO',
        firestoreUser: firestoreUser
      });
      
      console.log('🔍 Detailed firestoreUser check:', {
        firestoreUserExists: !!firestoreUser,
        firestoreUserKeys: firestoreUser ? Object.keys(firestoreUser) : 'no user',
        shippingAddressExists: !!firestoreUser?.shippingAddress,
        shippingAddressKeys: firestoreUser?.shippingAddress ? Object.keys(firestoreUser.shippingAddress) : 'no address',
        stateValue: firestoreUser?.shippingAddress?.state,
        stateType: typeof firestoreUser?.shippingAddress?.state
      });
      
      // Normalize state to abbreviation for backend compatibility
      const normalizedAddress = {
        ...firestoreUser.shippingAddress,
        state: firestoreUser.shippingAddress.state === 'Colorado' ? 'CO' : firestoreUser.shippingAddress.state
      };
      
      console.log('🔄 Normalized address for sales tax:', {
        original: firestoreUser.shippingAddress,
        normalized: normalizedAddress
      });
      
      const calculateSalesTax = httpsCallable(functions, 'calculateSalesTax');
      const result = await calculateSalesTax({
        shippingAddress: normalizedAddress,
        purchaseAmount: purchaseAmount,
        platformFee: cashExchangeFee
      });
      
      console.log('💰 Sales tax calculation result:', result.data);
      
      let salesTaxAmount = 0;
      let salesTaxRate = 0;
      let salesTaxState = null;
      
      if (result.data && result.data.enabled) {
        salesTaxAmount = result.data.taxAmount || 0;
        salesTaxRate = result.data.taxRatePercent || 0;
        salesTaxState = result.data.state || null;
        console.log('✅ Sales tax calculated:', { salesTaxAmount, salesTaxRate, salesTaxState });
      } else {
        console.log('ℹ️ Sales tax not enabled or no tax amount:', result.data);
        console.log('🔍 Sales tax disabled reason:', {
          enabled: result.data?.enabled,
          state: result.data?.state,
          note: result.data?.note,
          taxAmount: result.data?.taxAmount
        });
      }
      
      // Update the cost breakdown with sales tax
      const totalAmount = purchaseAmount + cashExchangeFee + salesTaxAmount;
      
      setCostBreakdown(prev => ({
        ...prev,
        salesTaxAmount,
        salesTaxRate,
        salesTaxState,
        totalAmount
      }));
      
    } catch (taxError) {
      console.error('❌ Error calculating sales tax:', taxError);
      // Continue without sales tax if calculation fails
    } finally {
      setCalculatingSalesTax(false);
    }
  };

  // Calculate costs when offer amount changes
  useEffect(() => {
    const amount = parseFloat(offerAmount);
    if (!isNaN(amount) && amount > 0) {
      const debounceTimer = setTimeout(() => {
        // Only calculate if firestoreUser is loaded
        if (firestoreUser) {
          console.log('🔄 Calculating cost breakdown with loaded firestoreUser');
          calculateCostBreakdown(amount);
        } else {
          console.log('⏳ Waiting for firestoreUser to load before calculating costs');
        }
      }, 500); // Debounce for 500ms
      
      return () => clearTimeout(debounceTimer);
    } else {
      setCostBreakdown(null);
      setShowCostBreakdown(false);
    }
  }, [offerAmount, membershipTier, firestoreUser]);

  // Trigger cost calculation when firestoreUser becomes available
  useEffect(() => {
    const amount = parseFloat(offerAmount);
    if (!isNaN(amount) && amount > 0 && firestoreUser) {
      console.log('🔄 firestoreUser now available, triggering cost calculation');
      calculateCostBreakdown(amount);
    }
  }, [firestoreUser]); // Only depend on firestoreUser

  if (notLoggedIn) {
    return (
      <div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ maxWidth: '500px', width: '100%', backgroundColor: '#e8f4fd', border: '2px solid #5A9ECB', borderRadius: '12px', padding: '2rem', textAlign: 'center', fontFamily: 'Nunito, sans-serif' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a5276', marginBottom: '1rem' }}>Log In to Make a Purchase Offer</h2>
          <p style={{ fontSize: '1rem', color: '#2c3e50', lineHeight: '1.6', marginBottom: '1.5rem' }}>You need to be logged in to make a purchase offer.</p>
          {/* Return here (with ?targetPedalId) after login: email links land on
              this page. Saved on the click only; see utils/loginReturn. */}
          <Link to="/login" onClick={() => rememberLoginReturn()} style={{ display: 'inline-block', backgroundColor: '#5A9ECB', color: 'white', fontWeight: '600', padding: '0.75rem 2rem', border: 'none', borderRadius: '6px', fontSize: '1rem', textDecoration: 'none', fontFamily: 'Nunito, sans-serif' }}>Go to Login</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' }}>
        <div className="text-center">Loading purchase details...</div>
      </div>
    );
  }

  if (error && !targetPedal) {
    return (
      <div className="p-4" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' }}>
        <div className="text-center text-red-600">{error}</div>
        <div className="text-center mt-4">
          <button 
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:underline"
          >
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="purchase-offer-page" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', padding: '0 1rem' }}>
      <style>{`
        /* Desktop: larger images */
        @media (min-width: 769px) {
          .pedal-large-image {
            width: 480px !important;
            height: 480px !important;
          }
        }
        
        @media (max-width: 768px) {
          .purchase-offer-page {
            padding: 0 0.5rem !important;
          }
          
          .purchase-pedal-details-box {
            padding: 1rem !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          
          .desktop-top-section {
            display: none !important;
          }
          
          .mobile-image-section {
            display: block !important;
          }
          
          .mobile-profile-make-container {
            display: grid !important;
            grid-template-columns: 1fr auto !important;
            gap: 2rem !important;
            align-items: flex-start !important;
            margin-bottom: 0.5rem !important;
          }
          
          .mobile-left-column {
            grid-column: 1 !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 1rem !important;
          }
          
          .mobile-make-model-section {
            margin: 0 !important;
          }
          
          .mobile-make-model-section h2 {
            margin-bottom: 0.35rem !important;
          }
          
          .mobile-profile-section {
            flex-shrink: 0 !important;
            padding-left: 0 !important;
            grid-column: 2 !important;
          }
          
          .mobile-username-btn {
            display: block !important;
            text-align: center !important;
            margin-left: auto !important;
            margin-right: auto !important;
            max-width: 140px !important;
          }
          
          .mobile-membership-badge {
            display: block !important;
            text-align: center !important;
          }
          
          .mobile-profile-img {
            margin-left: auto !important;
            margin-right: auto !important;
          }
          
          .mobile-profile-rating {
            display: flex !important;
            justify-content: center !important;
          }
          
          .mobile-pedal-details {
            display: block !important;
            grid-column: 1 !important;
          }
        }
        
        @media (min-width: 769px) {
          .mobile-image-section {
            display: none !important;
          }
          
          .mobile-profile-make-container {
            display: none !important;
          }
          
          .purchase-offer-note-textarea {
            width: 100% !important;
            min-width: auto !important;
            box-sizing: border-box !important;
          }
          
          /* Move button left to avoid sidebar */
          .back-to-top-btn {
            right: 160px !important;
          }
        }
        
        @media (max-width: 768px) {
          .purchase-offer-note-textarea {
            width: 100% !important;
            min-width: auto !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          
          /* Add padding to footer so tab bar doesn't cover it */
          .purchase-offer-footer {
            padding-bottom: 100px !important;
          }
          
          /* Move back-to-top button above tab bar */
          .back-to-top-btn {
            bottom: 100px !important;
          }
        }
      `}</style>
      <div style={{ padding: '1rem 0' }}>
        {/* Header */}
        <h1 className="font-bold text-center mb-6" style={{ fontSize: '1.75rem' }}>Offer to buy</h1>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6" style={{ maxWidth: '900px', margin: '0 auto' }}>
            <p className="text-red-700">{error}</p>
          </div>
        )}

      {/* Pedal Information Box */}
        <div 
          className="mb-6 purchase-pedal-details-box"
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            padding: '2rem',
            maxWidth: '880px',
            margin: '2.5rem auto 1.5rem auto',
            boxSizing: 'border-box'
          }}
        >
          {/* Desktop: Top Section - Large Image on Left, User Profile + Pedal Details on Right */}
          <div className="desktop-top-section" style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', marginBottom: '3rem', maxWidth: '100%' }}>
            
            {/* Left Side: Large Top Image */}
            <div 
              style={{ 
                flexShrink: 0
              }}
            >
              {targetPedal?.images?.top ? (
                <img
                  className="pedal-large-image"
                  src={targetPedal.images.top}
                  alt='Top View'
                  style={{
                    width: '480px',
                    height: '480px',
                    objectFit: 'cover',
                    borderRadius: '10px',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.02)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              ) : (
                <img
                  className="pedal-large-image"
                  src={placeholderFor(targetPedal || {}).url}
                  onError={(e) => { if (!e.currentTarget.src.endsWith(SKETCH_PLACEHOLDER_URL)) e.currentTarget.src = SKETCH_PLACEHOLDER_URL; }}
                  alt='Photos coming'
                  style={{
                    width: '480px',
                    height: '480px',
                    objectFit: 'cover',
                    borderRadius: '10px',
                    opacity: 0.6
                  }}
                />
              )}
            </div>
            
            {/* Right Side: User Profile + Pedal Details */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.25rem', minWidth: 0, maxWidth: '100%' }}>
              
              {/* Owner Profile Section */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                
                {/* Username */}
                <button
                  onClick={() => navigate(`/profile/${targetPedal?.ownerId}`)}
                  style={{ 
                    fontSize: '1.15rem', 
                    color: '#374151', 
                    textAlign: 'left',
                    fontWeight: '700',
                    fontFamily: 'Nunito, sans-serif',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    padding: '0',
                    marginBottom: '0.25rem'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  {sellerInfo.username}
                </button>
                
                {/* Membership Badge */}
                <div style={{ marginBottom: '0.35rem' }}>
                  {getMembershipBadge(sellerInfo.membershipTier)}
                </div>
                
                {/* Profile Image with Badges */}
                <div style={{ position: 'relative', width: '50px', height: '50px', marginBottom: '0.35rem' }}>
                  <button
                    onClick={() => navigate(`/profile/${targetPedal?.ownerId}`)}
                    style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      border: '2px solid #D1D5DB',
                      cursor: 'pointer',
                      padding: '0',
                      background: 'none',
                      overflow: 'hidden',
                      transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    <img
                      src={sellerInfo.photoURL || '/images/default-avatar.png'}
                      alt='User Avatar'
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        objectFit: 'cover',
                      }}
                    />
                  </button>
                  {/* Membership Badge - Top Left */}
                  {getMembershipProfileBadge(sellerInfo.membershipTier)}
                  
                  {/* Trade Count Badge - Bottom Right */}
                  {sellerInfo.badge !== null && sellerInfo.badge !== undefined && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '-3px',
                        right: '-3px',
                        backgroundColor: 'gold',
                        color: '#000',
                        fontWeight: 'bold',
                        borderRadius: '50%',
                        width: '18px',
                        height: '18px',
                        fontSize: '0.65rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid #fff',
                        boxShadow: '0 0 5px rgba(0,0,0,0.3)'
                      }}
                    >
                      {sellerInfo.badge}
                    </div>
                  )}
                </div>
                
                {/* User Rating */}
                {sellerInfo.averageRating > 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.875rem' }}>
                    <span style={{ color: '#f59e0b' }}>★</span>
                    <span style={{ fontWeight: '600', color: '#374151' }}>
                      {sellerInfo.averageRating.toFixed(1)}
                    </span>
                  </div>
                ) : (
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                    No ratings yet
                  </div>
                )}
              </div>
              
              {/* Pedal Brand and Model */}
              <div style={{ lineHeight: '1.0', textAlign: 'left', marginTop: '1.25rem', marginBottom: '1.75rem' }}>
                <h2 style={{ fontSize: '1.875rem', fontWeight: 'bold', margin: '0 0 0.15rem 0', padding: '0', lineHeight: '1.0', color: '#374151' }}>
                  {targetPedal?.make}
                </h2>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#6b7280', margin: '0', padding: '0', lineHeight: '1.0' }}>
                  {targetPedal?.model}
                </h3>
              </div>
              
              {/* Pedal Details */}
              <div style={{ fontSize: '1.05rem', color: '#374151', textAlign: 'left' }}>
                {targetPedal?.price && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Price:</strong> ${targetPedal.price}</p>
                )}
                {targetPedal?.condition && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Condition:</strong> {targetPedal.condition}</p>
                )}
                {targetPedal?.effectTypes && targetPedal.effectTypes.length > 0 && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}>
                    <strong>Effect Type{targetPedal.effectTypes.length > 1 ? 's' : ''}:</strong> {targetPedal.effectTypes.join(', ')}
                  </p>
                )}
                {targetPedal?.decade && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Decade:</strong> {targetPedal.decade}</p>
                )}
                {targetPedal?.color && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Color:</strong> {targetPedal.color}</p>
                )}
                {targetPedal?.location && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Location:</strong> {targetPedal.location}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Mobile: Large Image at Top */}
          <div className="mobile-image-section" style={{ display: 'none' }}>
            {targetPedal?.images?.top ? (
              <img
                className="pedal-large-image"
                src={targetPedal.images.top}
                alt='Top View'
                style={{
                  width: '100%',
                  height: 'auto',
                  maxWidth: '100%',
                  objectFit: 'cover',
                  borderRadius: '10px',
                  marginBottom: '2.5rem'
                }}
              />
            ) : (
              <img
                className="pedal-large-image"
                src={placeholderFor(targetPedal || {}).url}
                onError={(e) => { if (!e.currentTarget.src.endsWith(SKETCH_PLACEHOLDER_URL)) e.currentTarget.src = SKETCH_PLACEHOLDER_URL; }}
                alt='Photos coming'
                style={{
                  width: '100%',
                  height: 'auto',
                  maxWidth: '100%',
                  objectFit: 'cover',
                  borderRadius: '10px',
                  opacity: 0.6,
                  marginBottom: '2.5rem'
                }}
              />
            )}
          </div>
          
          {/* Mobile: Profile on Right, Make/Model + Pedal Details on Left */}
          <div className="mobile-profile-make-container" style={{ display: 'none' }}>
            {/* Left Column: Make/Model + Pedal Details */}
            <div className="mobile-left-column">
              {/* Mobile Make/Model Section */}
              <div className="mobile-make-model-section" style={{ lineHeight: '1.0', textAlign: 'left' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: '0 0 1rem 0', padding: '0', lineHeight: '1.0', color: '#374151' }}>
                  {targetPedal?.make}
                </h2>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#6b7280', margin: '0', padding: '0', lineHeight: '1.0' }}>
                  {targetPedal?.model}
                </h3>
              </div>
              
              {/* Mobile Pedal Details (below make/model on left) */}
              <div className="mobile-pedal-details" style={{ fontSize: '1.05rem', color: '#374151', textAlign: 'left' }}>
                {targetPedal?.price && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Price:</strong> ${targetPedal.price}</p>
                )}
                {targetPedal?.condition && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Condition:</strong> {targetPedal.condition}</p>
                )}
                {targetPedal?.effectTypes && targetPedal.effectTypes.length > 0 && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}>
                    <strong>Effect Type{targetPedal.effectTypes.length > 1 ? 's' : ''}:</strong> {targetPedal.effectTypes.join(', ')}
                  </p>
                )}
                {targetPedal?.decade && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Decade:</strong> {targetPedal.decade}</p>
                )}
                {targetPedal?.color && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Color:</strong> {targetPedal.color}</p>
                )}
                {targetPedal?.location && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Location:</strong> {targetPedal.location}</p>
                )}
              </div>
            </div>
            
            {/* Right Column: Mobile Profile Section */}
            <div className="mobile-profile-section">
              {/* Username */}
              <button
                className="mobile-username-btn"
                onClick={() => navigate(`/profile/${targetPedal?.ownerId}`)}
                style={{ 
                  fontSize: '1.15rem', 
                  color: '#374151', 
                  fontWeight: '700',
                  fontFamily: 'Nunito, sans-serif',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  padding: '0',
                  marginBottom: '0.25rem'
                }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
              >
                {sellerInfo.username}
              </button>
              
              {/* Membership Badge */}
              <div className="mobile-membership-badge" style={{ marginBottom: '0.35rem' }}>
                {getMembershipBadge(sellerInfo.membershipTier)}
              </div>
              
              {/* Profile Image with Badges */}
              <div className="mobile-profile-img" style={{ position: 'relative', width: '50px', height: '50px', marginBottom: '0.35rem' }}>
                <button
                  onClick={() => navigate(`/profile/${targetPedal?.ownerId}`)}
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    border: '2px solid #D1D5DB',
                    cursor: 'pointer',
                    padding: '0',
                    background: 'none',
                    overflow: 'hidden',
                    transition: 'opacity 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  <img
                    src={sellerInfo.photoURL || '/images/default-avatar.png'}
                    alt='User Avatar'
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                </button>
                {getMembershipProfileBadge(sellerInfo.membershipTier)}
                
                {sellerInfo.badge !== null && sellerInfo.badge !== undefined && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '-3px',
                      right: '-3px',
                      backgroundColor: 'gold',
                      color: '#000',
                      fontWeight: 'bold',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      fontSize: '0.65rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #fff',
                      boxShadow: '0 0 5px rgba(0,0,0,0.3)'
                    }}
                  >
                    {sellerInfo.badge}
                  </div>
                )}
              </div>
              
              {/* User Rating */}
              {sellerInfo.averageRating > 0 ? (
                <div className="mobile-profile-rating" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.875rem' }}>
                  <span style={{ color: '#f59e0b' }}>★</span>
                  <span style={{ fontWeight: '600', color: '#374151' }}>
                    {sellerInfo.averageRating.toFixed(1)}
                  </span>
                </div>
              ) : (
                <div className="mobile-profile-rating" style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                  No ratings yet
                </div>
              )}
            </div>
          </div>
          
          {/* Centered: View Full Listing Link */}
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <Link 
              to={`/listing/${targetPedal?.id}`} 
              className='hover:underline text-base inline-block'
              style={{ color: '#5A9ECB', fontSize: '1rem' }}
            >
              View Full Listing
            </Link>
          </div>
          
          {/* Description */}
          {targetPedal?.description && (
            <div style={{ marginTop: '1rem' }}>
              <h4 className='font-semibold mb-3 text-center'>Description</h4>
              <div style={{
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                padding: '16px',
                backgroundColor: '#f9fafb'
              }}>
                <p className='text-gray-700' style={{ 
                  margin: 0,
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {targetPedal.description}
                </p>
              </div>
            </div>
          )}

          {/* Purchase Offer Form */}
          <div style={{ marginTop: '1.5rem' }}>
            <div className='bg-gray-50 p-4 rounded-lg border'>
              <h4 className='font-semibold mb-3' style={{ textAlign: 'left', fontSize: '1.1rem' }}>Your Purchase Offer</h4>

              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ fontWeight: '500', fontSize: '0.85rem', color: '#374151', whiteSpace: 'nowrap' }}>Amount ($)</label>
                  <input
                    type="text"
                    value={offerAmount}
                    onChange={(e) => setOfferAmount(formatCurrencyInput(e.target.value))}
                    placeholder="0"
                    className="border border-gray-300 focus:border-blue-500 focus:outline-none"
                    style={{ flex: 1, padding: '8px 12px', fontSize: '0.95rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                  />
                </div>
              </div>

              {calculatingCosts && (
                <div className="text-center text-sm text-gray-500 py-2">Calculating costs...</div>
              )}

              {showCostBreakdown && costBreakdown && !calculatingCosts && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '12px' }} />
                  <h5 style={{ fontWeight: '600', fontSize: '0.95rem', color: '#111827', marginTop: 0, marginBottom: '10px', textAlign: 'left' }}>Your cost breakdown</h5>
                  <div style={{ fontSize: '0.85rem', color: '#374151' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span>Cash payment</span>
                      <span>${costBreakdown.purchaseAmount.toFixed(2)}</span>
                    </div>
                    {costBreakdown.cashExchangeFee > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#6b7280' }}>
                        <span>Cash processing fee ({costBreakdown.cashFeePercentage}%)</span>
                        <span>${costBreakdown.cashExchangeFee.toFixed(2)}</span>
                      </div>
                    )}
                    {firestoreUser?.shippingAddress && (
                      <>
                        {calculatingSalesTax && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#6b7280' }}>
                            <span>Sales Tax</span>
                            <span>Calculating...</span>
                          </div>
                        )}
                        {!calculatingSalesTax && costBreakdown.salesTaxAmount > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                            <span>{costBreakdown.salesTaxState} Sales Tax ({costBreakdown.salesTaxRate}%)</span>
                            <span>${costBreakdown.salesTaxAmount.toFixed(2)}</span>
                          </div>
                        )}
                      </>
                    )}
                    {!firestoreUser?.shippingAddress && (
                      <div style={{ fontSize: '0.75rem', color: '#ea580c', marginTop: '8px', padding: '8px', backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '6px' }}>
                        <strong>Add your shipping address</strong> to your profile to see sales tax calculation
                      </div>
                    )}
                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '0.95rem' }}>
                      <span>Total you'll pay</span>
                      <span style={{ color: '#111827' }}>${costBreakdown.totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                  {membershipTier === 'free' && (
                    <div style={{ fontSize: '0.75rem', color: '#4b5563', textAlign: 'center', marginTop: '12px', padding: '8px', backgroundColor: '#fefce8', border: '1px solid #fde68a', borderRadius: '6px' }}>
                      <strong>Click <a href="/profile" style={{ color: '#5A9ECB' }} className="hover:opacity-80 underline">here</a> to become a paid member</strong> and receive reduced platform fees and promoted matching!
                    </div>
                  )}
                </div>
              )}

              <div className="mb-4" style={{ overflow: 'hidden' }}>
                <div style={{ marginBottom: '0.5rem', textAlign: 'left' }}>
                  <label className="font-semibold">Note (optional):</label>
                </div>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a short note..."
                  maxLength={500}
                  rows={4}
                  className="w-full border border-gray-300 focus:border-blue-500 focus:outline-none resize-none purchase-offer-note-textarea"
                  style={{ padding: '12px', fontSize: '16px', borderRadius: '6px', lineHeight: '1.5', boxSizing: 'border-box', width: '100%' }}
                />
              </div>

              {error && (
                <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded">
                  <p className="text-red-600 text-xs">{error}</p>
                </div>
              )}
            </div>

            {!offerSent ? (
              <>
              {!addressComplete && (
                <p style={{ color: '#dc2626', fontSize: '0.95rem', marginTop: '1rem', marginBottom: '0', lineHeight: '1.4', textAlign: 'center' }}>
                  A shipping address is required before submitting this offer.{' '}
                  <button
                    type="button"
                    onClick={() => setShowAddressModal(true)}
                    style={{ color: '#dc2626', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit' }}
                  >
                    Add address
                  </button>
                </p>
              )}
              {/* Heads-up when the seller hasn't been on in a while (30+
                  days by their last active week; see utils/inactivity). */}
              <div style={{ marginTop: '1rem' }}>
                <InactiveOwnerNotice ownerName={targetUser?.username} lastActiveWeek={targetUser?.lastActiveWeek} anchorRef={submitButtonRef} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '1rem' }}>
                <button
                  ref={submitButtonRef}
                  onClick={handleSubmit}
                  disabled={submitting || !offerAmount || !addressComplete}
                  style={{
                    backgroundColor: (submitting || !offerAmount || !addressComplete) ? '#9CA3AF' : '#C9A227',
                    color: 'white',
                    border: 'none',
                    borderRadius: '9999px',
                    padding: '8px 24px',
                    fontFamily: 'Nunito, sans-serif',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    cursor: (submitting || !offerAmount || !addressComplete) ? 'not-allowed' : 'pointer',
                    opacity: (submitting || !offerAmount || !addressComplete) ? 0.6 : 1,
                    transition: 'opacity 0.2s'
                  }}
                  onMouseEnter={(e) => { if (!submitting && offerAmount && addressComplete) e.target.style.opacity = '0.8'; }}
                  onMouseLeave={(e) => { if (!submitting && offerAmount && addressComplete) e.target.style.opacity = '1'; }}
                >
                  {submitting ? 'Submitting...' : 'Submit Offer'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  style={{
                    backgroundColor: '#E5E7EB',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '9999px',
                    padding: '8px 24px',
                    fontFamily: 'Nunito, sans-serif',
                    fontSize: '0.95rem',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  Cancel
                </button>
              </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', marginTop: '24px' }}>
                <p style={{ color: '#6B7280', fontSize: '16px', marginBottom: '8px' }}>
                  Your offer has been sent!
                </p>
                <p style={{ color: '#6B7280', fontSize: '14px' }}>
                  Redirecting to Trade Center...
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Report Bug Section */}
        <div className='text-center border-t border-gray-200 purchase-offer-footer' style={{ maxWidth: '900px', margin: '64px auto 0 auto', paddingTop: '1rem' }}>
          <p className='text-gray-600 text-sm'>
            Need help or found a bug?{' '}
            <button 
              onClick={() => openContact({ category: 'support' })}
              style={{ 
                color: '#5A9ECB',
                background: 'none',
                border: 'none',
                padding: 0,
                font: 'inherit',
                cursor: 'pointer'
              }}
              className='hover:underline'
            >
              Contact Support
            </button>
          </p>
        </div>

        {/* Back to Top Button */}
        {showBackToTop && (
          <button
            onClick={scrollToTop}
            className="back-to-top-btn"
            style={{
              position: 'fixed',
              bottom: '30px',
              right: '30px',
              backgroundColor: '#5A9ECB',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#4A8AB0';
              e.target.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#5A9ECB';
              e.target.style.transform = 'scale(1)';
            }}
            aria-label="Back to top"
          >
            ⌃
          </button>
        )}

        {/* Inline shipping-address modal: opens only when the user clicks
            Submit Offer without a complete address. */}
        <AddressRequiredModal
          isOpen={showAddressModal}
          onClose={() => setShowAddressModal(false)}
          onSaved={() => setShowAddressModal(false)}
          user={firestoreUser}
          context="purchase-offer"
        />
      </div>
    </div>
  );
};

export default PurchaseOffer;
